import React, { useState, useEffect, useRef } from 'react';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { ProgressSpinner } from 'primereact/progressspinner';
import apiClient from '../../../stateManagers/services/axiosInterceptor';
import { GlobalInputField } from '../../../components/GlobalComponents/GlobalInputField';
import { GlobalTextArea } from '../../../components/GlobalComponents/GlobalTextArea';
import { GlobalButton } from '../../../components/GlobalComponents/GlobalButton';
import { useSchoolDetailsStore } from '../../../stateManagers/hooks/useSchoolDetailsStore';

interface AddEventFormProps {
  open: boolean;
  onClose: () => void;
  eventId?: string | null;
  onSuccess: (shouldResetPage?: boolean) => void;
}

interface EventFormData {
  title: string;
  body: string;
  event_date: string;
  event_time: string;
  event_incharge_id: string;
  is_holiday: boolean;
}

const SectionHeader: React.FC<{ icon: string; title: string }> = ({ icon, title }) => (
  <div style={{
    display: 'flex', alignItems: 'center', gap: '10px',
    marginBottom: '16px', paddingBottom: '12px',
    borderBottom: '1px solid rgba(229,231,235,0.6)',
  }}>
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      width: '32px', height: '32px', borderRadius: '8px',
      background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)', color: 'white',
    }}>
      <i className={icon} style={{ fontSize: '15px' }} />
    </div>
    <span style={{ color: '#1e3a8a', fontWeight: 700, fontSize: '14px', letterSpacing: '0.02em', textTransform: 'uppercase' }}>
      {title}
    </span>
  </div>
);

const StyledDateTimeInput: React.FC<{
  label: string;
  type: 'date' | 'time';
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
  disabled?: boolean;
  icon: string;
}> = ({ label, type, value, onChange, required, disabled, icon }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
    <label style={{ fontSize: '13px', fontWeight: 600, color: '#374151' }}>
      {label}{required && <span style={{ color: '#ef4444', marginLeft: '3px' }}>*</span>}
    </label>
    <div style={{ position: 'relative' }}>
      <i className={`pi ${icon}`} style={{
        position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)',
        color: '#9ca3af', fontSize: '14px', pointerEvents: 'none',
      }} />
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        disabled={disabled}
        style={{
          width: '100%', padding: '10px 12px 10px 36px',
          border: '1px solid #d1d5db', borderRadius: '10px',
          fontSize: '14px', color: '#1f2937', background: disabled ? '#f9fafb' : 'white',
          outline: 'none', transition: 'border-color 0.18s',
          fontFamily: 'inherit', cursor: disabled ? 'not-allowed' : 'text',
          boxSizing: 'border-box',
        }}
        onFocus={e => { e.currentTarget.style.borderColor = '#3b82f6'; }}
        onBlur={e => { e.currentTarget.style.borderColor = '#d1d5db'; }}
      />
    </div>
  </div>
);

const AddEventForm: React.FC<AddEventFormProps> = ({ open, onClose, eventId, onSuccess }) => {
  const toast = useRef<Toast>(null);
  const { schoolDetails } = useSchoolDetailsStore();

  const [formData, setFormData] = useState<EventFormData>({
    title: '', body: '', event_date: '', event_time: '',
    event_incharge_id: '',
    is_holiday: false,
  });
  const [loading,   setLoading]   = useState(false);
  const [fetching,  setFetching]  = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    if (open && eventId) fetchEventDetails();
    else if (open) resetForm();
  }, [open, eventId]);

  useEffect(() => {
    if (isClosing) {
      document.querySelector('.p-dialog')?.classList.add('dialog-closing');
      document.querySelector('.p-dialog-mask')?.classList.add('mask-closing');
    }
  }, [isClosing]);

  const fetchEventDetails = async () => {
    setFetching(true);
    try {
      const response = await apiClient.get(`/api/events?event_id=${eventId}`);
      if (response.data.status === 'success') {
        const ev = response.data.event;
        setFormData({
          title:             ev.title             ?? '',
          body:              ev.body              ?? '',
          event_date:        ev.event_date        ?? '',
          event_time:        ev.event_time        ? ev.event_time.substring(0, 5) : '',
          event_incharge_id: ev.event_incharge_id ? String(ev.event_incharge_id) : '',
          is_holiday:        !!ev.is_holiday,
        });
      } else {
        toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to load event details', life: 5000 });
        handleClose();
      }
    } catch {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to load event details', life: 5000 });
      handleClose();
    } finally {
      setFetching(false);
    }
  };

  const resetForm = () => {
    setFormData({ title: '', body: '', event_date: '', event_time: '', event_incharge_id: '', is_holiday: false });
  };

  const handleClose = () => {
    if (loading) return;
    setIsClosing(true);
    setTimeout(() => { onClose(); setIsClosing(false); }, 400);
  };

  const handleSubmit = async () => {
    if (!formData.title.trim()) {
      toast.current?.show({ severity: 'error', summary: 'Validation Error', detail: 'Title is required', life: 5000 });
      return;
    }
    if (!formData.body.trim()) {
      toast.current?.show({ severity: 'error', summary: 'Validation Error', detail: 'Description is required', life: 5000 });
      return;
    }
    if (!formData.event_date) {
      toast.current?.show({ severity: 'error', summary: 'Validation Error', detail: 'Event date is required', life: 5000 });
      return;
    }

    const schoolId  = schoolDetails?.school_id;
    const sessionId = schoolDetails?.current_session?.id;
    const session   = schoolDetails?.current_session?.session;

    if (!schoolId || !sessionId || !session) {
      toast.current?.show({
        severity: 'error', summary: 'Error',
        detail: 'School or current session details are missing. Please reload and try again.',
        life: 5000,
      });
      return;
    }

    setLoading(true);
    try {
      const isEdit      = !!eventId;
      const apiEndpoint = isEdit ? `/api/events/${eventId}` : '/api/events';
      const method      = isEdit ? 'put' : 'post';

      const payload: Record<string, string | number | boolean> = {
        title:      formData.title,
        body:       formData.body,
        event_date: formData.event_date,
        is_holiday: formData.is_holiday,   // ← boolean, not 1/0
        school_id:  schoolId,
        session_id: sessionId,
        session:    session,
      };
      if (formData.event_time)        payload.event_time        = formData.event_time;
      if (formData.event_incharge_id) payload.event_incharge_id = formData.event_incharge_id;

       console.log('Event payload:', payload);

      const res = await apiClient[method](apiEndpoint, payload);
      console.log('Event response:', res.data);

      toast.current?.show({
        severity: 'success', summary: 'Success',
        detail: isEdit ? 'Event updated successfully!' : 'Event added successfully!',
        life: 5000,
      });

      setTimeout(() => { resetForm(); onSuccess(!isEdit); handleClose(); }, 300);
    } catch (err: any) {
      const message = err.response?.data?.message || `Failed to ${eventId ? 'update' : 'add'} event`;
      toast.current?.show({ severity: 'error', summary: 'Error', detail: message, life: 5000 });
    } finally {
      setLoading(false);
    }
  };

  const set = (key: keyof EventFormData) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setFormData(prev => ({ ...prev, [key]: e.target.value }));

  const headerTemplate = () => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '8px 0' }}>
      <div style={{
        width: '54px', height: '54px', borderRadius: '14px',
        background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: 'white', fontSize: '24px', fontWeight: 700,
        boxShadow: '0 8px 24px rgba(59,130,246,0.3)', flexShrink: 0, border: '3px solid white',
      }}>
        <i className={eventId ? 'pi pi-pencil' : 'pi pi-calendar-plus'} />
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: '20px', fontWeight: 700, color: '#1e40af', lineHeight: 1.2, marginBottom: '4px' }}>
          {eventId ? 'Edit Event' : 'Add New Event'}
        </div>
        <div style={{ fontSize: '13px', color: '#64748b', fontWeight: 500 }}>
          {eventId ? 'Update event details below' : 'Schedule a new event for your school'}
        </div>
      </div>
    </div>
  );

  return (
    <>
      <Toast ref={toast} position="top-right" />
      <Dialog
        visible={open && !isClosing}
        onHide={handleClose}
        header={headerTemplate}
        style={{ width: '90vw', maxWidth: '720px', borderRadius: '24px', overflow: 'hidden' }}
        contentStyle={{
          padding: '18px',
          background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)',
          maxHeight: 'calc(90vh - 120px)', overflowY: 'auto',
          border: '12px solid rgba(255,255,255,0.8)',
          borderRadius: '0 0 24px 24px', borderTop: 'none',
        }}
        headerStyle={{
          background: 'white', borderBottom: '1px solid #e5e7eb',
          padding: '20px 24px', borderRadius: '24px 24px 0 0',
          borderLeft: '12px solid rgba(255,255,255,0.8)',
          borderRight: '12px solid rgba(255,255,255,0.8)',
          borderTop: '12px solid rgba(255,255,255,0.8)',
          margin: '-12px -12px 0 -12px',
        }}
        modal dismissableMask={!loading} draggable={false} resizable={false} blockScroll
      >
        <style>{`
          .p-dialog-mask { backdrop-filter: blur(4px); background-color: rgba(0,0,0,0.25) !important; animation: maskFadeIn 0.35s ease; }
          .p-dialog-mask.mask-closing { animation: maskFadeOut 0.4s ease !important; }
          @keyframes maskFadeIn { from { opacity:0; } to { opacity:1; } }
          @keyframes maskFadeOut { from { opacity:1; } to { opacity:0; } }
          .p-dialog { animation: dialogOpen 0.35s cubic-bezier(0.4,0,0.2,1); box-shadow: 0 25px 50px -12px rgba(0,0,0,0.25) !important; }
          .p-dialog.dialog-closing { animation: dialogClose 0.4s cubic-bezier(0.4,0,0.2,1) !important; }
          @keyframes dialogOpen { from { opacity:0; transform:scale(0.92) translateY(-20px); } to { opacity:1; transform:scale(1) translateY(0); } }
          @keyframes dialogClose { from { opacity:1; transform:scale(1) translateY(0); } to { opacity:0; transform:scale(0.88) translateY(25px); } }
        `}</style>

        {fetching ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 20px', gap: '16px' }}>
            <ProgressSpinner style={{ width: '40px', height: '40px' }} strokeWidth="4" />
            <span style={{ color: '#64748b', fontSize: '14px', fontWeight: 600 }}>Loading event details...</span>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

            <div style={{ background: 'rgba(255,255,255,0.8)', borderRadius: '16px', padding: '20px', border: '1px solid rgba(59,130,246,0.1)', boxShadow: '0 4px 16px rgba(0,0,0,0.05)' }}>
              <SectionHeader icon="pi pi-info-circle" title="Event Information" />
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <GlobalInputField
                  label="Title" value={formData.title}
                  onChange={set('title')}
                  placeholder="Enter event title" required disabled={loading} icon="pi pi-pencil"
                />
                <GlobalTextArea
                  label="Description" value={formData.body}
                  onChange={set('body')}
                  placeholder="Describe the event..."
                  required disabled={loading} rows={5} autoResize
                />
              </div>
            </div>

            <div style={{ background: 'rgba(255,255,255,0.8)', borderRadius: '16px', padding: '20px', border: '1px solid rgba(59,130,246,0.1)', boxShadow: '0 4px 16px rgba(0,0,0,0.05)' }}>
              <SectionHeader icon="pi pi-clock" title="Schedule" />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <StyledDateTimeInput
                  label="Event Date" type="date"
                  value={formData.event_date}
                  onChange={v => setFormData(prev => ({ ...prev, event_date: v }))}
                  required disabled={loading} icon="pi pi-calendar"
                />
                <StyledDateTimeInput
                  label="Event Time (optional)" type="time"
                  value={formData.event_time}
                  onChange={v => setFormData(prev => ({ ...prev, event_time: v }))}
                  disabled={loading} icon="pi pi-clock"
                />
              </div>

              <div style={{
                marginTop: '12px', padding: '10px 14px', borderRadius: '10px',
                background: 'rgba(30,58,138,0.05)', border: '1px solid rgba(30,58,138,0.12)',
                display: 'flex', alignItems: 'center', gap: '8px',
              }}>
                <i className="pi pi-info-circle" style={{ fontSize: '13px', color: '#3b82f6', flexShrink: 0 }} />
                <span style={{ fontSize: '12px', color: '#64748b' }}>
                  Events within <strong>30 minutes</strong> of an existing event on the same date will be rejected.
                </span>
              </div>

              <div
                style={{
                  marginTop: '12px', padding: '12px 14px', borderRadius: '10px',
                  background: formData.is_holiday ? 'rgba(251,191,36,0.08)' : 'rgba(30,58,138,0.03)',
                  border: `1px solid ${formData.is_holiday ? 'rgba(251,191,36,0.35)' : 'rgba(30,58,138,0.10)'}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  transition: 'all 0.18s',
                  userSelect: 'none',
                }}
                onClick={() => !loading && setFormData(prev => ({ ...prev, is_holiday: !prev.is_holiday }))}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 20 }}>🏖</span>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: formData.is_holiday ? '#b45309' : '#374151' }}>
                      Mark as holiday
                    </div>
                    <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 1 }}>
                      Highlights this day on the calendar
                    </div>
                  </div>
                </div>
                <div style={{
                  width: 40, height: 22, borderRadius: 11, position: 'relative', flexShrink: 0,
                  background: formData.is_holiday ? '#d97706' : '#d1d5db',
                  transition: 'background 0.2s',
                }}>
                  <div style={{
                    position: 'absolute', top: 3,
                    left: formData.is_holiday ? 21 : 3,
                    width: 16, height: 16, borderRadius: '50%', background: 'white',
                    transition: 'left 0.2s',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                  }} />
                </div>
              </div>
            </div>

            <div style={{ background: 'rgba(255,255,255,0.8)', borderRadius: '16px', padding: '20px', border: '1px solid rgba(59,130,246,0.1)', boxShadow: '0 4px 16px rgba(0,0,0,0.05)' }}>
              <SectionHeader icon="pi pi-user" title="Event Incharge" />
              <GlobalInputField
                label="Incharge User ID (optional)" value={formData.event_incharge_id}
                onChange={set('event_incharge_id')}
                placeholder="Enter teacher / admin user ID"
                disabled={loading} icon="pi pi-id-card"
              />
              <div style={{
                marginTop: '10px', padding: '10px 14px', borderRadius: '10px',
                background: 'rgba(15,118,110,0.05)', border: '1px solid rgba(15,118,110,0.15)',
                display: 'flex', alignItems: 'center', gap: '8px',
              }}>
                <i className="pi pi-shield" style={{ fontSize: '13px', color: '#0f766e', flexShrink: 0 }} />
                <span style={{ fontSize: '12px', color: '#64748b' }}>
                  Incharge must have a <strong>teacher</strong>, <strong>admin</strong>, <strong>principal</strong>, or <strong>superadmin</strong> role.
                </span>
              </div>
            </div>

            <div style={{ textAlign: 'center', marginTop: '8px' }}>
              <GlobalButton
                label={loading ? (eventId ? 'Updating...' : 'Adding...') : (eventId ? 'Update Event' : 'Add Event')}
                onClick={handleSubmit} variant="primary" size="medium"
                disabled={loading} loading={loading} fullWidth={false}
              />
            </div>

          </div>
        )}
      </Dialog>
    </>
  );
};

export default AddEventForm;